import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import MediaPreview from '../../../components/media-preview/MediaPreview';
import { MediaItem } from '../../../app/services/mediaItems';
import {
  MediaPreviewHandle,
  MediaPreviewProps,
  SizeMode,
} from '../../../types/media-preview';
import { ZipFileEntry, downloadFilesAsZip } from '../../../utils/downloadFilesAsZip';
import { getMediaUrl } from '../previewMediaUtils';
import { MaOptionSlot, MaSegment, MaSequence, stripExtension } from './maTypes';
import { csvTextToBlob, serializeMaTimeChartCsv } from './serializeMaCsv';
import { useMaAudioEngine } from './useMaAudioEngine';

/**
 * MA スライド: CSV の動画 1 行分のプレビュー。
 *
 * 見た目は通常の動画スライド（MediaPreview）と同じだが、再生は
 * useMaAudioEngine が video 要素と Web Audio を束ねて制御し、
 * CSV で紐づいたオーディオクリップをミックス再生する。
 *
 * 書き出し系の固有機能:
 * - 一括ダウンロード: 動画 + 紐づくオーディオ + タイムチャート CSV
 *  （ショット全動画・絶対フレーム・`<MAファイル名>_TC.csv`）の zip に差し替え。
 *   未選択のオプション候補ファイルも zip 直下に同梱する（フォルダ分けしない）
 * - レンダリング書き出し（テロップ付き動画 / ミックス動画 / WAV / CSV）は
 *   Preview.tsx の「書き出し」メニュー → MaExportModal に集約している
 *
 * ref には MediaPreviewHandle 互換の複合ハンドルを公開するため、
 * Preview.tsx の既存機構（キーボード操作 / タイムライン / コメントピン）が
 * そのまま動作する。getCurrentTime / seekTo は「動画 0F 基準の表示時刻」を返す。
 */

export interface MaSlideProps {
  segment: MaSegment;
  /** 表示中バリエーションかどうか（先行デコード / バッファ解放の制御） */
  active: boolean;
  /** シーケンス全体（一括ダウンロードのタイムチャート生成・オプション候補の同梱に使用） */
  sequence?: MaSequence;
  /** segment のシーケンス内 index（オプションスロットの帰属判定に使用） */
  segmentIndex?: number;
  /** 同梱タイムチャート CSV のファイル名ベース（MA ファイル名。未指定は 'MA'） */
  timeChartBaseName?: string;
  sizeMode?: SizeMode;
  style?: Record<string, unknown>;
  /** MEDIA_PREVIEW_FULL_PROPS 互換の外観 props */
  mediaPreviewProps?: Partial<MediaPreviewProps>;
  onPlayStateChange?: (_playing: boolean) => void;
}

/**
 * セグメントの動画 + 紐づくオーディオを zip エントリへ展開（同一アイテムは 1 回だけ）。
 * オプションスロットの候補ファイルのうちタイムラインに載っていないもの
 * （= 選択中以外の残り候補）もフォルダ分けせず zip 直下に含める。
 */
function buildSegmentZipEntries(segment: MaSegment, optionSlots: MaOptionSlot[]): ZipFileEntry[] {
  const entries: ZipFileEntry[] = [];
  const seenIds = new Set<string>();
  const usedNames = new Set<string>();

  const push = (item: MediaItem) => {
    if (seenIds.has(item.id)) return;
    seenIds.add(item.id);
    // 別アイテムの同名ファイルは連番サフィックスで zip 内衝突を回避
    let path = item.file_name;
    if (usedNames.has(path)) {
      const stem = stripExtension(item.file_name);
      const ext = item.file_name.slice(stem.length);
      let n = 2;
      while (usedNames.has(`${stem} (${n})${ext}`)) n += 1;
      path = `${stem} (${n})${ext}`;
    }
    usedNames.add(path);
    entries.push({ path, file: item });
  };

  // 動画は全表示区間分（本編 + 選択中候補の動画）を含める
  segment.videoSpans.forEach(span => push(span.videoItem));
  segment.clips.forEach(clip => push(clip.audioItem));
  // 選択中候補は上の走査で追加済み（seenIds）のため、ここでは残りだけが入る
  optionSlots.forEach(slot =>
    slot.choices.forEach(choice => {
      choice.clips.forEach(clip => push(clip.audioItem));
      if (choice.videoItem) push(choice.videoItem);
    }),
  );
  return entries;
}

const MaSlide = forwardRef<MediaPreviewHandle, MaSlideProps>(
  (
    {
      segment,
      active,
      sequence,
      segmentIndex,
      timeChartBaseName,
      sizeMode,
      style,
      mediaPreviewProps,
      onPlayStateChange,
    },
    ref,
  ) => {
    const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);

    // MediaPreview マウント時に内部 video 要素を解決してエンジンへ渡す
    const attachInnerRef = useCallback((handle: MediaPreviewHandle | null) => {
      const el = handle?.getMediaElement?.() ?? null;
      setVideoEl(el instanceof HTMLVideoElement ? el : null);
    }, []);

    const { handle, displayVideoItem } = useMaAudioEngine({
      segment,
      videoEl,
      active,
      onPlayStateChange,
    });

    // ===== 一括 zip ダウンロード =====

    // 二重実行ガード
    const exportBusyRef = useRef(false);

    // 一括ダウンロード = 動画 + 紐づくオーディオ + タイムチャート CSV + オプション候補の zip
    const handleBundleDownload = useCallback(async () => {
      if (exportBusyRef.current) return;
      exportBusyRef.current = true;
      const toastId = toast.loading('一括ダウンロードを準備中…');
      try {
        const optionSlots =
          sequence?.optionSlots.filter(slot => slot.segmentIndex === segmentIndex) ?? [];
        const entries = buildSegmentZipEntries(segment, optionSlots);
        // タイムチャート CSV（ショット全動画・絶対フレーム）。表示中の segment
        //（編集ワーキングコピーの場合あり）は差し替えて表示内容と一致させる
        if (sequence) {
          const segments = sequence.segments.map((s, i) => (i === segmentIndex ? segment : s));
          const csv = serializeMaTimeChartCsv({ ...sequence, segments });
          entries.push({
            path: `${timeChartBaseName ?? 'MA'}_TC.csv`,
            blob: csvTextToBlob(csv),
          });
        }
        const zipName = `${stripExtension(segment.videoItem.file_name)}_MA.zip`;
        const result = await downloadFilesAsZip(entries, zipName, {
          onProgress: (processed, total) =>
            toast.loading(`ファイルを取得中… ${processed}/${total}`, { id: toastId }),
        });
        if (result.succeeded === 0) {
          toast.error('一括ダウンロードに失敗しました', { id: toastId });
        } else if (result.failed > 0) {
          toast.success(
            `一括ダウンロードを開始しました（${result.failed}件は取得できませんでした）`,
            { id: toastId },
          );
        } else {
          toast.success('一括ダウンロードを開始しました', { id: toastId });
        }
      } catch {
        toast.error('一括ダウンロードに失敗しました', { id: toastId });
      } finally {
        exportBusyRef.current = false;
      }
    }, [segment, sequence, segmentIndex, timeChartBaseName]);

    // ３点リーダー: ダウンロード項目を「一括ダウンロード」へリネームする
    // （書き出し系のメニューは呼び出し元の extraMenuItems で渡される）
    const mergedPreviewProps = useMemo<Partial<MediaPreviewProps>>(
      () => ({
        ...mediaPreviewProps,
        downloadMenuLabel: '一括ダウンロード',
      }),
      [mediaPreviewProps],
    );

    // ダウンロード動作の差し替え（標準の単体ダウンロード → 一括 zip）
    const overrideDownload = useCallback(
      () => void handleBundleDownload(),
      [handleBundleDownload],
    );

    useImperativeHandle(
      ref,
      () => ({
        play: () => handle.play(),
        pause: () => handle.pause(),
        seek: (seconds: number) => handle.seekTo(handle.getCurrentTime() + seconds),
        seekTo: (seconds: number) => handle.seekTo(seconds),
        getCurrentTime: () => handle.getCurrentTime(),
        getDuration: () => handle.getDuration(),
        clickToggle: () => handle.toggle(),
        getMediaElement: () => videoEl,
      }),
      [handle, videoEl],
    );

    // 表示中の動画 = エンジンの現在区間（候補動画の区間では src が差し替わり、
    // エンジンが loadedmetadata で位置を合わせて再開する）
    const item = displayVideoItem;
    return (
      <MediaPreview
        ref={attachInnerRef}
        src={getMediaUrl(item)}
        downloadUrl={item.original_url}
        thumbnailSrc={item.thumbnail}
        mediaType="video"
        sizeMode={sizeMode}
        style={style}
        {...mergedPreviewProps}
        loop={false}
        overrideDownload={overrideDownload}
        fileName={item.file_name}
        fileId={item.id}
        createdAt={item.updated_at}
      />
    );
  },
);

MaSlide.displayName = 'MaSlide';

export default MaSlide;
